import React from "react";

import { EDITOR_CONSTANTS } from "@perceptron/constants/editorConstants";
import { cn } from "@perceptron/utils/cn";
import { MediaSetContext } from "@perceptron/utils/types";
import { Node, Editor } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { EditorView } from "@tiptap/pm/view";
import { ReactNodeViewRenderer, NodeViewProps, NodeViewWrapper } from "@tiptap/react";

import { MediaSetCollection } from "@demo/components/MediaSetCollection";
import { useDropZone } from "@demo/hooks/useDropZone";
import { useMediaStore } from "@demo/store/media";
import { useUploadProgress } from "@demo/store/upload-progress";
import { Role } from "@demo/types/base";
import { MediaSetContainerAttributes, TipTapAttributeDefinition } from "@demo/types/media";
import { findMediaSet, mediaSetDragPluginKey } from "@demo/utils/media-set-drag";
import type { MediaSetDragMeta, MediaSetDragState } from "@demo/utils/media-set-drag";
import { animateElementPositions, captureElementPositions, prefersReducedMotion } from "@demo/utils/media-set-motion";

import { createNodeProtectionPlugin, ProtectedNodeConfig } from "./utils/node-protection";

function createMediaSetDropSlot(slotHeight: number) {
  const slot = document.createElement("div");
  slot.className =
    "media-set-drop-slot relative z-[1] my-1 flex min-h-11 w-full box-border items-center justify-center overflow-hidden rounded-xl border border-dashed border-[color-mix(in_srgb,var(--brand-blue)_42%,var(--border))] bg-[color-mix(in_srgb,var(--brand-blue)_6%,var(--background))] pointer-events-none";
  slot.style.height = `${Math.max(44, slotHeight)}px`;

  const landingMark = document.createElement("span");
  landingMark.className =
    "block h-[3px] w-9 origin-center scale-x-[.72] rounded-full bg-[var(--brand-blue)] opacity-[.65]";
  slot.append(landingMark);

  if (!prefersReducedMotion() && typeof slot.animate === "function") {
    slot.animate(
      [
        { opacity: 0, transform: "scale(0.985)" },
        { opacity: 1, transform: "scale(1)" },
      ],
      { duration: 150, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" },
    );
    landingMark.animate(
      [
        { opacity: 0.4, transform: "scaleX(0.55)" },
        { opacity: 0.7, transform: "scaleX(0.82)" },
      ],
      { duration: 190, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" },
    );
  }

  return slot;
}

export function shouldStopMediaSetNodeViewEvent(event: Event) {
  // Let ProseMirror's clipboard pipeline handle paste when an upload/add
  // control currently owns focus. Native asset drags are always suppressed;
  // the media set uses its own pointer-based drag interaction.
  if (event.type === "copy" || event.type === "cut" || event.type === "paste") return false;
  return true;
}

function readHistoryMediaIds(view: EditorView): Map<string, Set<string>> | null {
  const historyPlugin = view.state.plugins.find((plugin) => (plugin as unknown as { key?: string }).key === "history$");
  if (!historyPlugin) return null;

  const historyState = historyPlugin.getState(view.state) as
    | {
        done?: { items?: { forEach: (callback: (item: { step?: { toJSON?: () => unknown } }) => void) => void } };
        undone?: { items?: { forEach: (callback: (item: { step?: { toJSON?: () => unknown } }) => void) => void } };
      }
    | undefined;
  if (!historyState) return null;

  const mediaIdsBySet = new Map<string, Set<string>>();
  const visit = (value: unknown) => {
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }

    const record = value as Record<string, unknown>;
    if (
      record.type === EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER &&
      record.attrs &&
      typeof record.attrs === "object"
    ) {
      const attrs = record.attrs as Record<string, unknown>;
      if (typeof attrs.id === "string") {
        const ids = mediaIdsBySet.get(attrs.id) ?? new Set<string>();
        if (Array.isArray(attrs.mediaIds)) {
          attrs.mediaIds.forEach((id) => {
            if (typeof id === "string") ids.add(id);
          });
        }
        mediaIdsBySet.set(attrs.id, ids);
      }
    }
    Object.values(record).forEach(visit);
  };

  [historyState.done, historyState.undone].forEach((branch) => {
    branch?.items?.forEach((item) => {
      try {
        visit(item.step?.toJSON?.());
      } catch {
        // A history step that cannot serialize must not prevent editor updates.
      }
    });
  });

  return mediaIdsBySet;
}

export interface MediaSetContainerExtensionOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  HTMLAttributes: Record<string, any>;
  context?: MediaSetContext;
  role: Role;
  onFileDrop: (files: File[], mediaSetId: string, editor: Editor) => Promise<void>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    mediaSetContainer: {
      setMediaSetContainer: (attrs: MediaSetContainerAttributes) => ReturnType;
      addMediaToSet: (attrs: { containerId: string; mediaId: string }) => ReturnType;
      removeMediaFromSet: (attrs: { containerId: string; mediaId: string }) => ReturnType;
      reorderMediaInSet: (attrs: { containerId: string; mediaIds: string[] }) => ReturnType;
      deleteMediaSetContainer: (containerId: string) => ReturnType;
      startMediaSetDrag: (attrs: { containerId: string; slotHeight: number }) => ReturnType;
      updateMediaSetDropTarget: (attrs: { containerId: string; dropPos: number | null }) => ReturnType;
      moveMediaSetContainer: (attrs: { containerId: string }) => ReturnType;
      clearMediaSetDrag: () => ReturnType;
    };
  }
}

export const MediaSetContainerExtension = Node.create<MediaSetContainerExtensionOptions>({
  name: EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER,
  group: "block",
  atom: true,
  selectable: false,
  draggable: false,

  addOptions() {
    return {
      HTMLAttributes: {},
      context: MediaSetContext.DOCUMENT_EDITOR,
      role: Role.USER,
      onFileDrop: async () => {
        throw new Error("MediaSetContainerExtension: onFileDrop handler must be configured");
      },
    };
  },

  addAttributes() {
    // Define attributes using our shared type definition
    // This ensures consistency with the factory function and document converter
    const attributes: TipTapAttributeDefinition<MediaSetContainerAttributes> = {
      id: { default: null },
      mediaIds: { default: [] },
    };

    return attributes;
  },

  parseHTML() {
    return [{ tag: `div[data-type=${EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER}]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", { "data-type": EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER, ...HTMLAttributes }];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MediaSetContainerNodeView, {
      stopEvent: ({ event }) => shouldStopMediaSetNodeViewEvent(event),
    });
  },

  addProseMirrorPlugins() {
    const protectedNodeConfig: ProtectedNodeConfig = {
      nodeName: this.name,
      allowEmptyDeletion: false,
      isEmpty: () => false,
    };

    const mediaSetDragPlugin = new Plugin<MediaSetDragState>({
      key: mediaSetDragPluginKey,
      state: {
        init: () => ({ mediaSetId: null, dropPos: null, slotHeight: 0 }),
        apply: (tr, currentState) => {
          const meta = tr.getMeta(mediaSetDragPluginKey) as MediaSetDragMeta | undefined;
          if (meta?.type === "start") {
            return { mediaSetId: meta.mediaSetId, dropPos: null, slotHeight: meta.slotHeight };
          }
          if (meta?.type === "target") {
            return { ...currentState, dropPos: meta.dropPos };
          }
          if (meta?.type === "clear") return { mediaSetId: null, dropPos: null, slotHeight: 0 };
          if (tr.docChanged && currentState.dropPos !== null) {
            return { ...currentState, dropPos: tr.mapping.map(currentState.dropPos) };
          }
          return currentState;
        },
      },
      props: {
        decorations: (state) => {
          const { dropPos, mediaSetId, slotHeight } = mediaSetDragPluginKey.getState(state) ?? {
            dropPos: null,
            mediaSetId: null,
            slotHeight: 0,
          };
          if (dropPos === null || mediaSetId === null) return null;
          return DecorationSet.create(state.doc, [
            Decoration.widget(dropPos, () => createMediaSetDropSlot(slotHeight), {
              key: "media-set-drop-slot",
              side: -1,
            }),
          ]);
        },
      },
      view: (editorView) => {
        const syncDraftMediaSets = (view: EditorView) => {
          const documentMediaSets: Array<{ id: string; mediaIds: string[] }> = [];
          view.state.doc.descendants((node) => {
            if (
              node.type.name !== EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER ||
              typeof node.attrs.id !== "string"
            ) {
              return true;
            }
            documentMediaSets.push({
              id: node.attrs.id,
              mediaIds: Array.isArray(node.attrs.mediaIds) ? node.attrs.mediaIds : [],
            });
            return false;
          });

          const mediaStore = useMediaStore.getState();
          mediaStore.syncDraftMediaSetsFromDocument(this.options.role, documentMediaSets);
          if (mediaStore.hasUndoTombstones(this.options.role)) {
            mediaStore.pruneUndoTombstones(this.options.role, readHistoryMediaIds(view));
          }
        };

        syncDraftMediaSets(editorView);
        return {
          update: (view, previousState) => {
            if (view.state.doc === previousState.doc) return;
            syncDraftMediaSets(view);
          },
        };
      },
    });

    return [createNodeProtectionPlugin([protectedNodeConfig]), mediaSetDragPlugin];
  },

  addKeyboardShortcuts() {
    return {
      // Let select-all and undo/redo fall through to StarterKit's handlers
      // (History is enabled; node protection allows history transactions).
      "Mod-a": () => false,
      "Mod-z": () => false,
      "Mod-y": () => false,
      "Mod-Shift-z": () => false,
    };
  },

  addCommands() {
    return {
      startMediaSetDrag:
        ({ containerId, slotHeight }) =>
        ({ tr, dispatch }) => {
          if (!findMediaSet(tr.doc, this.name, containerId)) return false;
          if (!dispatch) return true;
          tr.setMeta(mediaSetDragPluginKey, {
            type: "start",
            mediaSetId: containerId,
            slotHeight: Number.isFinite(slotHeight) ? Math.max(44, slotHeight) : 44,
          } satisfies MediaSetDragMeta);
          return true;
        },

      updateMediaSetDropTarget:
        ({ containerId, dropPos }) =>
        ({ tr, dispatch, view }) => {
          const dragState = mediaSetDragPluginKey.getState(view.state);
          if (dragState?.mediaSetId !== containerId) return false;
          if (dropPos === dragState.dropPos || !dispatch) return true;

          const before = captureElementPositions(
            Array.from(view.dom.children).filter((element): element is HTMLElement => element instanceof HTMLElement),
          );
          tr.setMeta(mediaSetDragPluginKey, { type: "target", dropPos } satisfies MediaSetDragMeta);
          animateElementPositions(before, { duration: 170, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" });
          return true;
        },

      moveMediaSetContainer:
        ({ containerId }) =>
        ({ tr, dispatch, view }) => {
          const dragState = mediaSetDragPluginKey.getState(view.state);
          if (dragState?.mediaSetId !== containerId) return false;

          const source = findMediaSet(tr.doc, this.name, containerId);
          if (!source || dragState.dropPos === null) {
            if (dispatch) tr.setMeta(mediaSetDragPluginKey, { type: "clear" } satisfies MediaSetDragMeta);
            return false;
          }

          const mappedDropPos = tr.mapping.map(dragState.dropPos, -1);
          const insertionPos = mappedDropPos > source.pos ? mappedDropPos - source.node.nodeSize : mappedDropPos;
          if (insertionPos === source.pos) {
            if (dispatch) tr.setMeta(mediaSetDragPluginKey, { type: "clear" } satisfies MediaSetDragMeta);
            return true;
          }

          const before = dispatch
            ? captureElementPositions(
                Array.from(view.dom.children).filter(
                  (element): element is HTMLElement => element instanceof HTMLElement,
                ),
              )
            : null;
          tr.delete(source.pos, source.pos + source.node.nodeSize);
          tr.insert(insertionPos, source.node);
          tr.setMeta(mediaSetDragPluginKey, { type: "clear" } satisfies MediaSetDragMeta);
          if (before)
            animateElementPositions(before, {
              duration: 170,
              easing: "cubic-bezier(0.2, 0.8, 0.2, 1)",
              shouldSkip: (element) => {
                const movedSet = element.matches("[data-media-set-id]")
                  ? element
                  : element.querySelector<HTMLElement>("[data-media-set-id]");
                return movedSet?.dataset.mediaSetId === containerId;
              },
            });
          return true;
        },

      clearMediaSetDrag:
        () =>
        ({ tr, dispatch, view }) => {
          const dragState = mediaSetDragPluginKey.getState(view.state);
          if (!dragState?.mediaSetId) return true;
          if (!dispatch) return true;

          const before =
            dragState.dropPos === null
              ? null
              : captureElementPositions(
                  Array.from(view.dom.children).filter(
                    (element): element is HTMLElement => element instanceof HTMLElement,
                  ),
                );
          tr.setMeta(mediaSetDragPluginKey, { type: "clear" } satisfies MediaSetDragMeta);
          if (before) animateElementPositions(before, { duration: 170, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" });
          return true;
        },

      setMediaSetContainer:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              id: attrs.id,
              mediaIds: [],
            },
          }),

      addMediaToSet:
        ({ containerId, mediaId }) =>
        ({ tr }) => {
          const match = findMediaSet(tr.doc, this.name, containerId);
          if (!match) return false;
          const { node: containerNode, pos: containerPos } = match;

          const currentIds: string[] = containerNode.attrs.mediaIds || [];
          if (currentIds.includes(mediaId)) return true; // already stored

          const newAttrs = {
            ...containerNode.attrs,
            mediaIds: [...currentIds, mediaId],
          };
          tr.setNodeMarkup(containerPos, undefined, newAttrs);
          return true;
        },
      removeMediaFromSet:
        ({ containerId, mediaId }) =>
        ({ tr }) => {
          const match = findMediaSet(tr.doc, this.name, containerId);
          if (!match) return false;
          const { node: containerNode, pos: containerPos } = match;

          const currentIds: string[] = containerNode.attrs.mediaIds || [];
          if (!currentIds.includes(mediaId)) return true;

          const newAttrs = {
            ...containerNode.attrs,
            mediaIds: currentIds.filter((id) => id !== mediaId),
          };

          tr.setNodeMarkup(containerPos, undefined, newAttrs);
          return true;
        },

      reorderMediaInSet:
        ({ containerId, mediaIds }) =>
        ({ tr }) => {
          const match = findMediaSet(tr.doc, this.name, containerId);
          if (!match) return false;
          const { node: containerNode, pos: containerPos } = match;

          const currentIds: string[] = containerNode.attrs.mediaIds || [];
          if (currentIds.length === mediaIds.length && currentIds.every((id, index) => id === mediaIds[index]))
            return true;

          tr.setNodeMarkup(containerPos, undefined, {
            ...containerNode.attrs,
            mediaIds,
          });
          return true;
        },

      deleteMediaSetContainer:
        (containerId) =>
        ({ tr }) => {
          const match = findMediaSet(tr.doc, this.name, containerId);
          if (!match) return false;

          tr.delete(match.pos, match.pos + match.node.nodeSize);
          tr.setMeta("forceDelete", true);
          tr.setMeta("targetNodeType", EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER);
          return true;
        },
    };
  },
});

const MediaSetContainerNodeView: React.FC<NodeViewProps> = (props) => {
  const { editor, node } = props;

  // Check if this media set is currently uploading
  const uploadProgress = useUploadProgress(node.attrs.id);
  const isUploading = uploadProgress?.isUploading ?? false;

  const { isDragOver, isDisabled, handlers } = useDropZone({
    onFilesDropped: async (files) => {
      // Call the handler passed from the extension options
      await props.extension.options.onFileDrop(files, node.attrs.id, editor);
    },
    disabled: isUploading,
  });

  // Get context from extension options
  const context = props.extension.options.context;

  return (
    <NodeViewWrapper>
      <div
        className={cn(
          isDragOver && isDisabled && "cursor-not-allowed rounded-xl opacity-60",
          isDragOver && !isDisabled && "rounded-xl ring-2 ring-primary ring-offset-2",
        )}
        {...handlers}
      >
        <MediaSetCollection
          context={context}
          editor={editor}
          mediaIds={node.attrs.mediaIds || []}
          mediaSetId={node.attrs.id}
        />
      </div>
    </NodeViewWrapper>
  );
};
